import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import { ALL_SETTINGS_VIEW_KEYS } from './constants/settingsTabs';
import ViewAsRoleBanner from './components/ViewAsRoleBanner';
import { loadSession, patchSession } from './lib/sessionState';
import useBackGuard from './hooks/useBackGuard';

// Import UI components
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Button } from './components/ui/button';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { Users, Building2, FileText, FolderOpen, Settings, DollarSign, Eye, Calendar as CalendarLucide, User, Shield, Package, FileSignature } from 'lucide-react';

// Import custom components
import CompanyProfile from './components/CompanyProfile';
import WeeklyTimesheet from './components/WeeklyTimesheet';
import PrintReports from './components/PrintReports';
import SettingsTab from './components/SettingsTab';
import BudgetTab from './components/BudgetTab';
import InvoicesTab from './components/invoices/InvoicesTab';
import Login from './components/Login';
import DashboardStats from './components/DashboardStats';
import RecentTimesheets from './components/RecentTimesheets';
import MyProfile from './components/MyProfile';
import ManpowerPlanning from './components/ManpowerPlanning';
import ISO9001Page from './components/iso9001/ISO9001Page';
import InventoryPage from './components/inventory/InventoryPage';
import AppHeader from './components/AppHeader';
import GlobalSearch, { useGlobalSearchHotkey } from './components/GlobalSearch';
import EmployeesTab from './components/EmployeesTab';
import CustomersTab from './components/CustomersTab';
import ProjectsTab from './components/ProjectsTab';
import ContractsTab from './components/ContractsTab';
import PublicSurveyForm from './components/PublicSurveyForm';
import PublicCandidateForm from './components/PublicCandidateForm';
import PublicRecordView from './components/PublicRecordView';
import CredentialsExpiryWidget from './components/CredentialsExpiryWidget';
import DashboardShortcuts from './components/DashboardShortcuts';
import PinShortcutButton from './components/PinShortcutButton';
import ShortcutBar from './components/ShortcutBar';
import FloatingPinButton from './components/FloatingPinButton';
import { NavProvider } from './contexts/NavContext';
import { ShortcutsProvider, useShortcutTarget } from './contexts/ShortcutsContext';
import { MAIN_TAB_META, autoColor } from './constants/shortcutTargets';

// Import Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider, usePermissions } from './hooks/usePermissions';
import { DisplayCurrencyProvider } from './hooks/useDisplayCurrency';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Dashboard Component
const Dashboard = () => {
  const { t } = useTranslation();
  const { user, logout, token, dataVersion } = useAuth();
  const { can, canAny, isSuperadmin } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [settings, setSettings] = useState(null);
  // Pick up where the user left off. Phones kill the tab when another app
  // needs memory (a phone call is enough), and the reload used to dump
  // everyone on the dashboard with their open sheet gone.
  const restored = useRef(loadSession());
  const [activeTab, setActiveTab] = useState(() => restored.current?.tab || 'dashboard');
  const [navContext, setNavContext] = useState(() => restored.current?.navContext || null);
  const [navHistory, setNavHistory] = useState([]);
  const [searchOpen, setSearchOpen] = useGlobalSearchHotkey();

  useBackGuard();

  // Baseline pinnable location: the open main tab. Nested screens register
  // a deeper target at a higher priority and win the pin button.
  const mainMeta = MAIN_TAB_META[activeTab] || { icon: 'star', perms: [] };
  useShortcutTarget({
    tab: activeTab,
    context: null,
    label: mainMeta.labelKey ? t(mainMeta.labelKey) : activeTab,
    icon: mainMeta.icon,
    color: autoColor(activeTab),
    perms: mainMeta.perms,
  }, 0);

  useEffect(() => { patchSession({ tab: activeTab, navContext }); }, [activeTab, navContext]);
  
  const [companyProfile, setCompanyProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [employeeLinkStatus, setEmployeeLinkStatus] = useState({ linked: false, employee: null });

  const navigateTo = useCallback((tab, context = null) => {
    setNavHistory(prev => [...prev.slice(-9), { tab: activeTab, context: navContext }]);
    setActiveTab(tab);
    setNavContext(context);
  }, [activeTab, navContext]);

  const goBack = useCallback(() => {
    if (navHistory.length === 0) return;
    const prev = navHistory[navHistory.length - 1];
    setNavHistory(h => h.slice(0, -1));
    setActiveTab(prev.tab);
    setNavContext(prev.context);
  }, [navHistory]);

  // Check if user is linked to employee profile
  const isLinkedToEmployee = employeeLinkStatus.linked || user?.role === 'admin' || user?.role === 'manager' || isSuperadmin;

  // Initial data fetch - runs once on mount
  useEffect(() => {
    let isMounted = true;
    
    const initializeApp = async () => {
      try {
        const linkStatusRes = await axios.get(`${API}/auth/me/employee`).catch(() => ({ data: { linked: false } }));
        if (isMounted) setEmployeeLinkStatus(linkStatusRes.data);
        
        const [employeesRes, customersRes, projectsRes, timesheetsRes, settingsRes, profileRes] = await Promise.all([
          axios.get(`${API}/employees`).catch(() => ({ data: [] })),
          axios.get(`${API}/customers`).catch(() => ({ data: [] })),
          axios.get(`${API}/projects`).catch(() => ({ data: [] })),
          axios.get(`${API}/timesheets`).catch(() => ({ data: [] })),
          axios.get(`${API}/settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/company/profile`).catch(() => ({ data: { name: 'Expertfix Sp. z o.o.', logo_file: null } }))
        ]);

        if (isMounted) {
          setEmployees(employeesRes.data);
          setCustomers(customersRes.data);
          setProjects(projectsRes.data);
          setTimesheets(timesheetsRes.data);
          setSettings(settingsRes.data);
          setCompanyProfile(profileRes.data);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        if (isMounted) setIsInitialized(true);
      }
    };

    initializeApp();
    return () => { isMounted = false; };
  }, []);

  // Arriving from a machine QR sticker (…/?machine=<id>): open that
  // project's Machines tab so the engineer can start the routine check.
  useEffect(() => {
    if (!isInitialized) return;
    const params = new URLSearchParams(window.location.search);
    const machineId = params.get('machine');
    if (!machineId) return;
    window.history.replaceState({}, '', window.location.pathname);
    axios.get(`${API}/machines/${machineId}`)
      .then((r) => {
        setActiveTab('projects');
        setNavContext({ openProjectId: r.data.project_id, projectTab: 'machines', focusMachineId: machineId });
      })
      .catch(() => {});
  }, [isInitialized]);

  const fetchData = useCallback(async () => {
    try {
      const [employeesRes, customersRes, projectsRes, timesheetsRes] = await Promise.all([
        axios.get(`${API}/employees`).catch(() => ({ data: [] })),
        axios.get(`${API}/customers`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/timesheets`).catch(() => ({ data: [] }))
      ]);
      setEmployees(employeesRes.data);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);
      setTimesheets(timesheetsRes.data);
    } catch (error) { console.error('Error fetching data:', error); }
  }, []);

  // Auto-refresh top-level cached data whenever dataVersion bumps
  // (employee/customer/project mutations from any component).
  useEffect(() => {
    if (!isInitialized || dataVersion === 0) return;
    fetchData();
  }, [dataVersion, isInitialized, fetchData]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch { setSettings({}); }
  }, []);

  const fetchCompanyProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/company/profile`);
      setCompanyProfile(response.data);
    } catch { setCompanyProfile({ name: 'Expertfix Sp. z o.o.', logo_file: null }); }
  }, []);

  return (
    <NavProvider navContext={navContext} navigateTo={navigateTo}>
    <div className="min-h-screen bg-slate-50">
      <ViewAsRoleBanner />
      <AppHeader user={user} logout={logout} companyProfile={companyProfile}
        onOpenSearch={() => setSearchOpen(true)} />
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} onNavigate={navigateTo} />
      <FloatingPinButton />

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ShortcutBar onNavigate={navigateTo} />
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setNavContext(null); }} className="space-y-6">
          <div className="flex items-start gap-1">
          <TabsList className="flex flex-wrap h-auto gap-1 p-1 bg-muted rounded-lg">
            <TabsTrigger value="dashboard" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-dashboard">
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.dashboard')}</span>
            </TabsTrigger>
            {isLinkedToEmployee && (
              <>
                {/* My Profile tab for regular employees, Employees tab for those with employee permissions */}
                {can('employees.view') ? (
                  <TabsTrigger value="employees" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-employees">
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.employees')}</span>
                  </TabsTrigger>
                ) : (
                  <TabsTrigger value="my-profile" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-my-profile">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.profile')}</span>
                  </TabsTrigger>
                )}
                {can('customers.view') && (
                  <TabsTrigger value="customers" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-customers">
                    <Building2 className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.customers')}</span>
                  </TabsTrigger>
                )}
                {can('projects.view') && (
                  <TabsTrigger value="projects" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-projects">
                    <FolderOpen className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.projects')}</span>
                  </TabsTrigger>
                )}
                {can('timesheets.view') && (
                  <TabsTrigger value="weekly-timesheets" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-weekly-timesheets">
                    <CalendarLucide className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.timesheets')}</span>
                  </TabsTrigger>
                )}
                {can('budget.view') && (
                  <TabsTrigger value="budget" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-budget">
                    <DollarSign className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.budget')}</span>
                  </TabsTrigger>
                )}
                {can('invoices.view') && (
                  <TabsTrigger value="invoices" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-invoices">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.invoices')}</span>
                  </TabsTrigger>
                )}
                {can('manpower.view') && (
                  <TabsTrigger value="manpower" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-manpower">
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.manpower')}</span>
                  </TabsTrigger>
                )}
                {canAny('iso_documents.view', 'iso_checklists.view', 'iso_test_reports.view') && (
                  <TabsTrigger value="iso9001" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-iso9001">
                    <Shield className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.iso')}</span>
                  </TabsTrigger>
                )}
                {canAny('contracts.view', 'contract_templates.view', 'client_stamp_signatories.view') && (
                  <TabsTrigger value="contracts" className="flex items-center space-x-2 px-3 py-2" data-testid="contracts-main-tab">
                    <FileSignature className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.contracts')}</span>
                  </TabsTrigger>
                )}
                {can('inventory.view') && (
                  <TabsTrigger value="inventory" className="flex items-center space-x-2 px-3 py-2" data-testid="inventory-main-tab">
                    <Package className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('nav.inventory')}</span>
                  </TabsTrigger>
                )}
              </>
            )}
            {can('reports.view') && (
              <TabsTrigger value="reports" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-reports">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.reports')}</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="company" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-company">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.company')}</span>
            </TabsTrigger>
            {canAny(...ALL_SETTINGS_VIEW_KEYS) && (
              <TabsTrigger value="settings" className="flex items-center space-x-2 px-3 py-2" data-testid="tab-settings">
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.settings')}</span>
              </TabsTrigger>
            )}
          </TabsList>
          <PinShortcutButton className="mt-1" />
          </div>

          {/* Go Back Banner */}
          {navHistory.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={goBack} className="text-xs text-muted-foreground hover:text-foreground" data-testid="go-back-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
                {t('auto.misc3.goBack')}
              </Button>
              <span className="text-xs text-muted-foreground">({navHistory.length})</span>
            </div>
          )}

          {/* Notification Banner for Users Not Linked to Employee */}
          {!isLinkedToEmployee && !can('employees.view') && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-amber-800">{t('auto.misc3.accountNotLinked')}</h3>
                  <p className="mt-1 text-sm text-amber-700">
                    Your account has not been linked to an employee profile yet. Please contact an administrator to link your account. 
                    Once linked, you will have access to timesheets, expenses, projects, and more.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            <DashboardShortcuts onNavigate={navigateTo} projects={projects} />
            <DashboardStats 
              employees={employees}
              projects={projects}
              customers={customers}
              timesheets={timesheets}
              onNavigate={setActiveTab}
            />
            {can('employees.view') && (
              <CredentialsExpiryWidget employees={employees} onNavigate={navigateTo} />
            )}
            <RecentTimesheets timesheets={timesheets} projects={projects} />
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees" className="space-y-6">
            <EmployeesTab employees={employees} user={user} fetchData={fetchData}
              onNavigateToISO={(employee) => navigateTo('iso9001', employee ? { isoSubTab: 'employees', employeeSearch: employee.name || employee.employee_code } : null)}
              navContext={navContext} />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-6">
            <CustomersTab customers={customers} user={user} fetchData={fetchData} navContext={navContext} />
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-6">
            <ProjectsTab projects={projects} customers={customers} employees={employees}
              settings={settings} user={user} fetchData={fetchData} navContext={navContext} />
          </TabsContent>

          {/* Weekly Timesheets Tab */}
          <TabsContent value="weekly-timesheets" className="space-y-6">
            <WeeklyTimesheet user={user} />
          </TabsContent>

          {/* Budget Tab */}
          <TabsContent value="budget" className="space-y-6">
            <BudgetTab 
              projects={projects} 
              employees={employees} 
              settings={settings}
              currentUser={user}
              navContext={navContext}
            />
          </TabsContent>

          {/* Invoices Tab */}
          {can('invoices.view') && (
            <TabsContent value="invoices" className="space-y-6">
              <InvoicesTab 
                projects={projects}
                customers={customers}
                currentUser={user}
              />
            </TabsContent>
          )}

          {/* Manpower Planning Tab */}
          <TabsContent value="manpower" className="space-y-6">
            <ManpowerPlanning 
              currentUser={user}
              employees={employees}
              projects={projects}
              settings={settings}
            />
          </TabsContent>

          {/* ISO 9001 Quality Management Tab */}
          <TabsContent value="iso9001" className="space-y-6">
            <ISO9001Page
              token={token}
              employees={employees}
              projects={projects}
              currentUser={user}
              navContext={navContext}
            />
          </TabsContent>

          {/* Contract Tracking */}
          <TabsContent value="contracts" className="space-y-6">
            <ContractsTab navContext={navContext} />
          </TabsContent>

          {/* Inventory Tab */}
          {can('inventory.view') && (
            <TabsContent value="inventory" className="space-y-6">
              <InventoryPage
                currentUser={user}
                employees={employees}
                projects={projects}
                navigateTo={navigateTo}
                navContext={navContext}
              />
            </TabsContent>
          )}

          {/* My Profile Tab - For users without Employees access */}
          {isLinkedToEmployee && !can('employees.view') && (
            <TabsContent value="my-profile" className="space-y-6">
              <MyProfile isAdmin={false} />
            </TabsContent>
          )}

          {/* Reports Tab */}
          {can('reports.view') && (
            <TabsContent value="reports" className="space-y-6">
              <PrintReports user={user} />
            </TabsContent>
          )}

          {/* Company Tab */}
          <TabsContent value="company" className="space-y-6">
            <CompanyProfile user={user} />
          </TabsContent>

          {/* Settings Tab */}
          {canAny(...ALL_SETTINGS_VIEW_KEYS) && (
            <TabsContent value="settings" className="space-y-6">
              <SettingsTab onSettingsChange={setSettings} />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
    </NavProvider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, token } = useAuth();
  
  console.log('ProtectedRoute render:', { isAuthenticated, loading, hasUser: !!user, hasToken: !!token });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <PermissionsProvider token={token}>
      <ShortcutsProvider>{children}</ShortcutsProvider>
    </PermissionsProvider>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <DisplayCurrencyProvider defaultCurrency="EUR">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/reset-password" element={<Login />} />
            {/* Public, token-gated customer satisfaction survey page.
                MUST be above the catch-all redirect below. */}
            <Route path="/survey/:token" element={<PublicSurveyForm />} />
            <Route path="/candidate-intake/:token" element={<PublicCandidateForm />} />
            <Route path="/p/:slug/:token" element={<PublicRecordView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        {/* Global toast host. Every toast.success/error/info call in
            the app requires this Toaster to be mounted somewhere in
            the tree — without it every notification is silently
            swallowed. Positioned top-right so it doesn't clash with
            the "Made with Emergent" badge at the bottom. */}
        <Toaster richColors position="top-right" closeButton />
      </DisplayCurrencyProvider>
    </AuthProvider>
  );
};

export default App;
